import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { FilterBarHandle } from '../../components/FilterBar';
import {
  getActiveHomeScrollElement,
  resetHomeFilterBarScrollPositions,
  toggleHomeFilterBarState,
} from './homeFilterBar.runtime';

type ToastType = 'success' | 'error' | 'info';

interface UseHomeFilterBarOptions {
  displayMode: string;
  gridScrollRef: RefObject<HTMLDivElement | null>;
  listScrollRef: RefObject<HTMLDivElement | null>;
  selectedTagIds: number[];
  selectedAuthorIds: number[];
  sortField: string;
  sortDirection: 'asc' | 'desc';
  clearFilters: () => void;
  setSortField: React.Dispatch<React.SetStateAction<string>>;
  setSortDirection: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>;
  showToast: (message: string, type?: ToastType) => void;
}

interface UseHomeFilterBarResult {
  filterBarComponentRef: RefObject<FilterBarHandle | null>;
  isFilterBarOpen: boolean;
  onClearFilters: () => void;
  handleFilterBarToggle: () => void;
  handleSortFieldChange: (field: string) => void;
  handleSortDirectionChange: (direction: 'asc' | 'desc') => void;
}

export const useHomeFilterBar = ({
  displayMode,
  gridScrollRef,
  listScrollRef,
  selectedTagIds,
  selectedAuthorIds,
  sortField,
  sortDirection,
  clearFilters,
  setSortField,
  setSortDirection,
  showToast,
}: UseHomeFilterBarOptions): UseHomeFilterBarResult => {
  const filterBarComponentRef = useRef<FilterBarHandle>(null);
  const [isFilterBarOpen, setIsFilterBarOpen] = useState(false);

  useEffect(() => {
    resetHomeFilterBarScrollPositions(gridScrollRef, listScrollRef);
    window.scrollTo(0, 0);
  }, [gridScrollRef, listScrollRef, selectedAuthorIds, selectedTagIds, sortDirection, sortField]);

  useEffect(() => {
    if (!isFilterBarOpen) {
      return;
    }

    const scrollElement = getActiveHomeScrollElement(displayMode, gridScrollRef, listScrollRef);

    const handleScroll = () => {
      filterBarComponentRef.current?.closeAllPanels();
    };

    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [displayMode, gridScrollRef, isFilterBarOpen, listScrollRef]);

  const onClearFilters = useCallback(() => {
    clearFilters();
    showToast('Filtres effacés', 'info');
    setIsFilterBarOpen(false);
  }, [clearFilters, showToast]);

  const handleFilterBarToggle = useCallback(() => {
    setIsFilterBarOpen(toggleHomeFilterBarState);
  }, []);

  const handleSortFieldChange = useCallback((field: string) => {
    setSortField(field);
    setIsFilterBarOpen(false);
  }, [setSortField]);

  const handleSortDirectionChange = useCallback((direction: 'asc' | 'desc') => {
    setSortDirection(direction);
    setIsFilterBarOpen(false);
  }, [setSortDirection]);

  return {
    filterBarComponentRef,
    isFilterBarOpen,
    onClearFilters,
    handleFilterBarToggle,
    handleSortFieldChange,
    handleSortDirectionChange,
  };
};

export default useHomeFilterBar;
